import React, { useState } from "react";
import { supabase } from "../supabase";
import { useNavigate } from "react-router-dom";
import { City, Country, State } from "country-state-city";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../styles/register.css";
import { friendlyAuthError, friendlyDataError } from "../lib/friendlyErrors";

function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [waitingForEmail, setWaitingForEmail] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [countries] = useState(Country.getAllCountries());
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [phone, setPhone] = useState("");

  const saveProfile = async (user, profile) => {
    return supabase.from("profiles").upsert(
      {
        id: user.id,
        email: user.email,
        first_name: profile.firstName,
        middle_name: profile.middleName,
        last_name: profile.lastName,
        phone: profile.phoneNumber,
        job_type: profile.jobType,
        organization_type: profile.organizationType,
        organization_name: profile.organizationName,
        country: profile.countryName,
        state: profile.stateName,
        city: profile.city,
      },
      {
        onConflict: "id",
      }
    );
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage("");

    const form = event.target;
    const firstName = form.firstName.value.trim();
    const middleName = form.middleName.value.trim();
    const lastName = form.lastName.value.trim();
    const emailValue = form.email.value.trim();
    const password = form.password.value;
    const confirmPassword = form.confirmPassword.value;
    const jobType = form.jobType.value;
    const organizationType = form.organizationType.value;
    const organizationName = form.organizationName.value.trim();
    const countryCode = form.country.value;
    const stateCode = form.state.value;
    const city = form.city.value;

    const countryName = Country.getAllCountries().find((country) => country.isoCode === countryCode)?.name;
    const stateName = State.getStatesOfCountry(countryCode).find((state) => state.isoCode === stateCode)?.name;
    const phoneNumber = phone.startsWith("+") ? phone : `+${phone}`;

    if (password !== confirmPassword) {
      setMessageType("error");
      setMessage("Passwords do not match.");
      setLoading(false);
      return;
    }

    const profile = {
      firstName,
      middleName,
      lastName,
      phoneNumber,
      jobType,
      organizationType,
      organizationName,
      countryName,
      stateName,
      city,
    };

    localStorage.setItem("tempProfile", JSON.stringify(profile));

    const { data, error } = await supabase.auth.signUp({
      email: emailValue,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/email-confirmation`,
        data: profile,
      },
    });

    if (error) {
      console.error("Registration failed:", error);
      setMessageType("error");
      setMessage(friendlyAuthError(error, "We could not create your account right now. Please try again."));
      setLoading(false);
      return;
    }

    if (data.session?.user) {
      const { error: profileError } = await saveProfile(data.session.user, profile);

      if (profileError) {
        console.error("Profile save failed after registration:", profileError);
        setMessageType("error");
        setMessage(friendlyDataError(profileError, "Your account was created, but we could not save your profile details."));
        setLoading(false);
        return;
      }

      localStorage.removeItem("tempProfile");
      setMessageType("success");
      setMessage("Account created successfully.");
      setTimeout(() => navigate("/"), 1500);
      setLoading(false);
      return;
    }

    setMessageType("success");
    setMessage(`Account created successfully. Please check ${emailValue} to verify your email.`);
    setRegisteredEmail(emailValue);
    setWaitingForEmail(true);
    setLoading(false);
  };

  const resendConfirmationEmail = async () => {
    if (!registeredEmail) {
      setMessageType("error");
      setMessage("Please enter your email and create the account again.");
      return;
    }

    setLoading(true);
    setMessage("");

    const { error } = await supabase.auth.resend({
      type: "signup",
      email: registeredEmail,
      options: {
        emailRedirectTo: `${window.location.origin}/email-confirmation`,
      },
    });

    if (error) {
      console.error("Verification email resend failed:", error);
      setMessageType("error");
      setMessage(friendlyAuthError(error, "We could not resend the verification email right now. Please try again."));
      setLoading(false);
      return;
    }

    setMessageType("success");
    setMessage(`Verification email resent to ${registeredEmail}.`);
    setLoading(false);
  };

  const checkEmailConfirmation = async () => {
    setLoading(true);

    const { data: userData, error } = await supabase.auth.getUser();
    if (error) {
      console.error("Email confirmation check failed:", error);
      setMessageType("error");
      setMessage(friendlyAuthError(error, "We could not check your account right now. Please try again."));
      setLoading(false);
      return;
    }

    const user = userData?.user;
    if (!user) {
      setMessageType("info");
      setMessage("Please verify your email first, then try again.");
      setLoading(false);
      return;
    }

    if (!user.email_confirmed_at) {
      setMessageType("info");
      setMessage("Your email has not been confirmed yet.");
      setLoading(false);
      return;
    }

    const tempProfile = JSON.parse(localStorage.getItem("tempProfile"));
    if (!tempProfile) {
      setMessageType("error");
      setMessage("We could not find your saved profile details.");
      setLoading(false);
      return;
    }

    const { error: profileError } = await saveProfile(user, tempProfile);

    if (profileError) {
      console.error("Profile save failed after email confirmation:", profileError);
      setMessageType("error");
      setMessage(friendlyDataError(profileError, "Your account was verified, but we could not save your profile details."));
      setLoading(false);
      return;
    }

    localStorage.removeItem("tempProfile");
    setMessageType("success");
    setMessage("Your email has been verified and your profile is ready.");
    setTimeout(() => navigate("/"), 1500);
    setLoading(false);
  };

  return (
    <section className="auth-section">
      <div className="auth-box">
        <div className="welcome-message">
          <h2>Create your account</h2>
          <p>Enter your details below to get started with Al Jabriyah Commercial.</p>
        </div>

        {message ? <div className={`login-message ${messageType}`}>{message}</div> : null}

        {!waitingForEmail ? (
          <form onSubmit={handleRegister}>
            <div className="form-row">
              <input name="firstName" placeholder="First name" required />
              <input name="middleName" placeholder="Middle name" />
              <input name="lastName" placeholder="Last name" required />
            </div>

            <PhoneInput
              country="sa"
              value={phone}
              onChange={(value) => setPhone(value)}
              enableSearch
              inputStyle={{ width: "100%", height: "45px" }}
            />

            <div className="form-row">
              <select
                name="country"
                required
                onChange={(event) => {
                  const countryCode = event.target.value;
                  const statesList = State.getStatesOfCountry(countryCode);

                  setStates(statesList || []);
                  setCities([]);
                  event.target.form.state.value = "";
                  event.target.form.city.value = "";
                }}
              >
                <option value="">Select country</option>
                {countries.map((country) => (
                  <option key={country.isoCode} value={country.isoCode}>
                    {country.name}
                  </option>
                ))}
              </select>

              <select
                name="state"
                required
                onChange={(event) => {
                  const stateCode = event.target.value;
                  const countryCode = event.target.form.country.value;

                  if (!countryCode) {
                    setMessageType("info");
                    setMessage("Please choose the country first.");
                    return;
                  }

                  setMessage("");
                  setCities(City.getCitiesOfState(countryCode, stateCode) || []);
                  event.target.form.city.value = "";
                }}
              >
                <option value="">Select state</option>
                {states.map((state) => (
                  <option key={state.isoCode} value={state.isoCode}>
                    {state.name}
                  </option>
                ))}
              </select>

              <select name="city" required>
                <option value="">Select city</option>
                {cities.map((city) => (
                  <option key={city.name} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>

            <input name="email" type="email" placeholder="Email address" required />
            <input name="password" type="password" placeholder="Password" required />
            <input name="confirmPassword" type="password" placeholder="Confirm password" required />

            <select name="jobType" required>
              <option value="">Job type</option>
              <option>Student</option>
              <option>Consultant</option>
              <option>Business</option>
            </select>

            <select name="organizationType" required>
              <option value="">Organization type</option>
              <option>Government</option>
              <option>Private</option>
              <option>Charity</option>
            </select>

            <input name="organizationName" placeholder="Organization name (optional)" />

            <button className="auth-btn" disabled={loading}>
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>
        ) : (
          <div style={{ marginTop: "20px" }}>
            <p>Please check your email, then click the button below once your account is verified.</p>

            <button className="auth-btn" onClick={checkEmailConfirmation} disabled={loading}>
              {loading ? "Checking..." : "I have verified my email"}
            </button>

            <button
              className="auth-btn"
              type="button"
              onClick={resendConfirmationEmail}
              disabled={loading}
              style={{ marginTop: "12px" }}
            >
              Resend verification email
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default Register;
